import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../api';
import { NOTE_MAX, reportContext, sendReport, type ReportKind } from '../diag';
import { errorText, serverErrorText, t } from '../i18n';
import { research } from '../research-client';

/**
 * The learner's own evidence references for this report (ticket 68): the
 * current research session, the active capture attempt and one attempt-scoped
 * replay ref. Only refs that actually exist travel — a research opt-out, a stopped or never-started attempt, or a
 * withheld moment reads back as explicitly missing or unknown on the
 * owner's side, never silently filled in. Consent-gated content
 * (generation/artifact records) never attaches; the refs' bodies stay in
 * their own stores. Non-participants send the same pre-existing scope as
 * before (note + masked context); the server drops anything outside the
 * ref grammar.
 */
function reportEvidenceRefs(): { sessionRef?: string; attemptRef?: string; replayRef?: string } {
  const attempt = research.currentAttempt();
  const sessionId = research.sessionId();
  const out: { sessionRef?: string; attemptRef?: string; replayRef?: string } = {};
  if (sessionId) out.sessionRef = `session:${sessionId}`;
  if (attempt) {
    out.attemptRef = `attempt:${attempt.attemptId}`;
    out.replayRef = `replay:${attempt.attemptId}`;
  }
  return out;
}

/**
 * "Something's wrong here" (v1 ticket 43): a problem, an idea or a question, a note of up to 2,000 characters, and
 * Send. The report carries the recent errors and screens, the masked route, the viewport and the look (`diag.ts`),
 * never content; the note is the only free text, and the sheet says who reads it. On a moment screen of the invited
 * beta, **Include this moment** (off by default) starts that moment's support share and names it.
 *
 * Opened with `openReport` from home's ⋯ menu and the moment screen, over whatever is on screen: a history entry
 * (`#report` on the same path), so Back closes it; `report-open` on <html> makes home's bar and panel inert.
 */
const KINDS: ReportKind[] = ['problem', 'idea', 'question'];
const HASH = '#report';

export interface ReportOpen {
  /** The moment on screen, offered as Include this moment when `share` (the beta's support share) is there. */
  momentId?: string;
  share?: boolean;
}

/** Opens the sheet over the current screen (once). */
export function openReport(o: ReportOpen = {}) {
  if (document.querySelector('report-sheet')) return;
  const el = document.createElement('report-sheet') as ReportSheet;
  el.momentId = o.momentId ?? '';
  el.share = !!o.share;
  el.from = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  document.body.append(el);
}

@customElement('report-sheet')
export class ReportSheet extends LitElement {
  @property({ attribute: false }) momentId = '';
  @property({ attribute: false }) share = false;
  /** Focus goes back here when the sheet closes. */
  from: HTMLElement | null = null;
  @state() private kind: ReportKind = 'problem';
  @state() private note = '';
  @state() private include = false;
  @state() private busy = false;
  @state() private error = '';
  @state() private result: 'sent' | 'saved' | '' = '';

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    if (location.hash !== HASH) history.pushState({ report: true }, '', `${location.pathname}${location.search}${HASH}`);
    document.documentElement.classList.add('report-open');
    window.addEventListener('popstate', this.onPop);
    // Capture, so Escape closes this sheet and not the moment panel under it.
    window.addEventListener('keydown', this.onKey, true);
    void this.updateComplete.then(() => this.querySelector<HTMLElement>('.report-note')?.focus());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.documentElement.classList.remove('report-open');
    window.removeEventListener('popstate', this.onPop);
    window.removeEventListener('keydown', this.onKey, true);
  }

  /** Back (or a close that went back): the entry is gone, so is the sheet. */
  private onPop = () => {
    if (location.hash !== HASH) this.shut();
  };

  private onKey = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    this.close();
  };

  private close = () => {
    if ((history.state as { report?: boolean } | null)?.report) {
      history.back();
      return;
    }
    // Opened straight at `#report`: drop the hash, no history entry to go back to.
    history.replaceState(null, '', `${location.pathname}${location.search}`);
    this.shut();
  };

  private shut() {
    const back = this.from;
    this.remove();
    queueMicrotask(() => { if (back?.isConnected) back.focus(); });
  }

  private send = async () => {
    const note = this.note.trim();
    if (!note || this.busy) return;
    this.busy = true;
    this.error = '';
    const withMoment = this.include && this.share && this.momentId ? this.momentId : '';
    const keptNote = note.slice(0, NOTE_MAX);
    try {
      const r = await sendReport({
        kind: this.kind, note: keptNote, ...reportContext(),
        ...reportEvidenceRefs(), ...(withMoment ? { momentId: withMoment } : {}),
      });
      // The moment is shared only once the report is in: a refused report shares nothing, and one kept on the
      // device for later shares nothing either (the owner sees its moment id, and may ask).
      if ('ok' in r && r.ok === 'sent' && withMoment) {
        try {
          await api.shareForSupport(withMoment);
        } catch { /* the report stands; the moment is simply not shared */ }
      }
      if ('ok' in r) {
        this.result = r.ok;
        // A refused or saved report must not lose the learner's words: keep
        // the note in the sheet so it can be sent again, and keep it verbatim
        // — never cleared, trimmed further, or replaced by a placeholder.
        if (r.ok === 'saved') this.note = keptNote;
        else this.note = '';
      } else this.error = serverErrorText(r.status, r.body);
    } catch (e) {
      this.error = errorText(e);
    } finally {
      this.busy = false;
    }
  };

  render() {
    const done = !!this.result;
    return html`
      <div class="moments-dim report-dim" aria-hidden="true" @click=${this.close}></div>
      <section class="moments-panel report-panel" role="dialog" aria-modal="true" aria-labelledby="report-title">
        <div class="moments-head">
          <h2 id="report-title">${t('report.title')}</h2>
          <button class="btn ghost icon report-close" @click=${this.close} aria-label=${t('report.close')}>✕</button>
        </div>
        ${done
          ? html`<p class="report-result" role="status">${t(this.result === 'sent' ? 'report.sent' : 'report.saved')}</p>
              ${this.result === 'saved' ? html`<p class="report-kept-note" dir="auto">${this.note}</p>
              <p class="muted small report-kept">${t('report.kept')}</p>` : nothing}
              <button class="btn block report-done" @click=${this.close}>${t('report.close')}</button>`
          : html`
            <p class="muted small report-lede">${t('report.lede')}</p>
            <div class="report-kinds" role="group" aria-label=${t('report.kind')}>
              ${KINDS.map((k) => html`<button class="btn small report-kind ${this.kind === k ? 'primary' : ''}" data-kind=${k}
                aria-pressed=${this.kind === k ? 'true' : 'false'} @click=${() => { this.kind = k; }}>${t(`report.${k}`)}</button>`)}
            </div>
            <textarea class="note report-note" dir="auto" maxlength=${NOTE_MAX} placeholder=${t('report.note')} aria-label=${t('report.note')}
              .value=${this.note} @input=${(e: Event) => { this.note = (e.target as HTMLTextAreaElement).value; }}></textarea>
            <div class="report-meta">
              <span class="muted small">${t('report.noteHint')}</span>
              <span class="muted small report-count">${t('report.count', { n: this.note.length, max: NOTE_MAX })}</span>
            </div>
            ${this.share && this.momentId
              ? html`<label class="report-include">
                  <input type="checkbox" .checked=${this.include} @change=${(e: Event) => { this.include = (e.target as HTMLInputElement).checked; }} />
                  <span>${t('report.include')}<span class="muted small">${t('report.includeHint')}</span></span>
                </label>`
              : nothing}
            ${this.error ? html`<p class="report-error" role="alert">${this.error}</p>` : nothing}
            <button class="btn primary block report-send" ?disabled=${this.busy || !this.note.trim()} @click=${this.send}>
              ${this.busy ? html`<span class="spinner"></span>` : t('report.send')}
            </button>`}
      </section>`;
  }
}
